import { Box, Typography, Button } from '@mui/material';
import { Sparkles, Flame, Crown, Sun } from 'lucide-react';
import PioneerBanner from '../components/PioneerBanner';

// Plan icon + background tint + gold-blended top gradient per plan
const PLAN_THEME = {
  trial:     { Icon: Sparkles, bg: '#f8f8f6', gradient: 'linear-gradient(90deg, transparent, rgba(90,105,120,0.18), rgba(154,114,9,0.22), rgba(90,105,120,0.18), transparent)' },
  basic:     { Icon: Flame,    bg: '#fdfbf6', gradient: 'linear-gradient(90deg, transparent, rgba(154,114,9,0.25), rgba(184,134,11,0.35), rgba(154,114,9,0.25), transparent)' },
  pro:       { Icon: Crown,    bg: '#f6f8fc', gradient: 'linear-gradient(90deg, transparent, rgba(30,58,95,0.2), rgba(154,114,9,0.28), rgba(30,58,95,0.2), transparent)' },
  community: { Icon: Sun,      bg: '#f9f6fc', gradient: 'linear-gradient(90deg, transparent, rgba(100,60,160,0.18), rgba(154,114,9,0.25), rgba(100,60,160,0.18), transparent)' },
};

// Hardware tiers — same for every plan, price combined per plan
const HW_TIERS = [
  {
    key: 'software',
    label: 'תוכנה בלבד',
    note: 'תביאו ציוד משלכם',
    oneTime: 0,
    extraMonthly: 0,
  },
  {
    key: 'minipc',
    label: '+ מיני-מחשב',
    note: 'נועל מסך, מתאושש אחרי הפסקת מתח',
    oneTime: 200,
    extraMonthly: 0,
    badge: 'מומלץ',
  },
  {
    key: 'screen',
    label: '+ מסך ומיני-מחשב',
    note: 'הגעה, התקנה וסיום',
    oneTime: 1000,
    extraMonthly: 0,
  },
  {
    key: 'full',
    label: '+ מסך, מחשב וסים',
    note: 'ללא צורך באינטרנט קיים',
    oneTime: 1000,
    extraMonthly: 12,
  },
];

// Returns { line1, line2 } for display inside the card
const bundlePrice = (plan, tier) => {
  const isFree = plan.price === 'חינם';
  const isCustom = plan.price === 'בואו נדבר';

  if (isCustom) {
    const l1 = 'לפי הצעה';
    const l2 = tier.oneTime ? `+ ₪${tier.oneTime} חד-פ` : null;
    return { line1: l1, line2: l2 };
  }

  if (isFree) {
    if (!tier.oneTime) return { line1: 'חינם', line2: null };
    return { line1: 'חינם', line2: `+ ₪${tier.oneTime} חד-פ` };
  }

  const base = parseInt(plan.price.replace('₪', ''));
  const monthly = base + tier.extraMonthly;
  const l1 = `₪${monthly}/חודש`;
  const l2 = tier.oneTime ? `+ ₪${tier.oneTime} חד-פ` : null;
  return { line1: l1, line2: l2 };
};

const PLANS = [
  {
    key: 'trial',
    name: 'התנסות',
    price: 'חינם',
    sub: '14 יום היכרות — ללא כרטיס אשראי',
    cta: 'התחל חינם',
    noHardware: true,
    features: [
      'גישה מלאה לכל הרכיבים',
      'מסך אחד',
      '2 מערכי תצוגה · 2 לוחות',
    ],
  },
  {
    key: 'basic',
    name: 'נֵר',
    price: '₪72',
    priceSub: 'לחודש',
    sub: 'לבית כנסת קטן עם מסך אחד',
    cta: 'בחר מסלול',
    features: [
      'מסך ציבורי אחד',
      '2 מערכי תצוגה למסך',
      '2 לוחות למערך תצוגה',
      'כל הרכיבים ללא הגבלה',
      'תמיכה תוך 5 ימי עסקים',
    ],
    notIncluded: ['היסטוריית גרסאות', 'ספריית רכיבים שמורים'],
  },
  {
    key: 'pro',
    name: 'מנורה',
    price: '₪101',
    priceSub: 'לחודש',
    sub: 'לבית כנסת עם יותר פעילות — גרסאות ומערכי תצוגה',
    cta: 'בחר מסלול',
    features: [
      'עד 3 מסכים',
      '5 מערכי תצוגה למסך',
      '5 לוחות למערך תצוגה',
      'כל הרכיבים ללא הגבלה',
      'היסטוריית 5 גרסאות לכל לוח',
      'ספריית רכיבים שמורים',
      'תמיכה תוך 48 שעות',
    ],
  },
  {
    key: 'community',
    name: 'מָאוֹר',
    price: 'בואו נדבר',
    sub: 'למוסדות גדולים עם צרכים מורכבים',
    cta: 'צור קשר',
    features: [
      'מסכים ללא הגבלה',
      'מערכי תצוגה ולוחות ללא הגבלה',
      'גרסאות ורכיבים שמורים ללא הגבלה',
      'הדרכה ואונבורדינג אישי',
      'מנהל חשבון ייעודי',
      'התאמות מיוחדות לפי דרישה',
      'SLA מוגדר + תמיכה ישירה',
    ],
  },
];

const PlanCard = ({ plan, onCta }) => {
  const isPioneer = plan.key === 'basic';
  const theme = PLAN_THEME[plan.key];
  const { Icon } = theme;
  const dim = 'rgba(30,58,95,0.07)';
  const textMuted = '#8a94a0';
  const textMain = '#1e3a5f';

  return (
    <Box
      sx={{
        bgcolor: theme.bg,
        borderRadius: 3.5,
        p: 3.5,
        border: isPioneer ? '1.5px solid rgba(154,114,9,0.35)' : '1.5px solid rgba(30,58,95,0.1)',
        position: 'relative',
        transition: 'transform 0.25s ease, box-shadow 0.25s ease',
        '&:hover': {
          transform: 'translateY(-5px)',
          boxShadow: '0 16px 40px rgba(30,58,95,0.15)',
        },
        display: 'flex',
        flexDirection: 'column',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '3px',
          borderRadius: '14px 14px 0 0',
          background: theme.gradient,
        },
      }}
    >
      {/* Plan icon */}
      <Box sx={{ mb: 1.5 }}>
        <Icon
          size={22}
          strokeWidth={1.5}
          color="#9a7209"
          style={{ opacity: 0.55 }}
        />
      </Box>

      {/* Plan name */}
      <Typography sx={{
        fontFamily: 'Secular One, sans-serif',
        fontSize: '1.1rem',
        color: '#9a7209',
        mb: 0.5,
      }}>
        {plan.name}
      </Typography>

      {/* Price */}
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, mb: 0.5 }}>
        <Typography sx={{
          fontFamily: 'Frank Ruhl Libre, serif',
          fontSize: plan.price.includes('₪') ? '2.5rem' : '1.6rem',
          fontWeight: 700,
          color: '#1e3a5f',
          lineHeight: 1,
        }}>
          {plan.price}
        </Typography>
        {plan.priceSub && (
          <Typography sx={{
            color: textMuted,
            fontFamily: 'Heebo, sans-serif',
            fontSize: '0.85rem',
          }}>
            {plan.priceSub}
          </Typography>
        )}
      </Box>

      <Typography sx={{
        color: textMuted,
        fontFamily: 'Heebo, sans-serif',
        fontSize: '0.82rem',
        mb: isPioneer ? 1 : 3,
        minHeight: '1.5em',
      }}>
        {plan.sub}
      </Typography>

      {isPioneer && (
        <Typography sx={{
          fontFamily: 'Heebo, sans-serif',
          fontSize: '0.78rem',
          fontWeight: 600,
          color: '#9a7209',
          mb: 2,
        }}>
          בתכנית חלוצים: 4 חודשים חינם →
        </Typography>
      )}

      <Box sx={{ borderTop: `1px solid ${dim}`, mb: 2.5 }} />

      {/* Features */}
      <Box sx={{ flex: 1, mb: 3 }}>
        {plan.features.map((f, i) => {
          const isIndented = f.startsWith('  — ');
          const text = isIndented ? f.replace('  — ', '') : f;
          return (
            <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1, pr: isIndented ? 2 : 0 }}>
              {!isIndented && (
                <Box component="span" sx={{ color: '#28c840', fontSize: '0.85rem', mt: '2px', flexShrink: 0 }}>✓</Box>
              )}
              {isIndented && (
                <Box component="span" sx={{ color: '#8a94a0', fontSize: '0.75rem', mt: '2px', flexShrink: 0 }}>·</Box>
              )}
              <Typography sx={{
                color: isIndented ? '#8a94a0' : '#1e3a5f',
                fontFamily: 'Heebo, sans-serif',
                fontSize: isIndented ? '0.78rem' : '0.88rem',
                lineHeight: 1.6,
              }}>
                {text}
              </Typography>
            </Box>
          );
        })}

        {plan.notIncluded?.map((f, i) => (
          <Box key={`no-${i}`} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1, opacity: 0.4 }}>
            <Box component="span" sx={{ color: '#c53030', fontSize: '0.85rem', mt: '2px', flexShrink: 0 }}>✗</Box>
            <Typography sx={{
              color: '#5a6978',
              fontFamily: 'Heebo, sans-serif',
              fontSize: '0.88rem',
              lineHeight: 1.6,
            }}>
              {f}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* ── Hardware bundle options ─────────────────────────────── */}
      {!plan.noHardware && <Box sx={{
        borderTop: `1px solid ${dim}`,
        mb: 3,
        pt: 2,
      }}>
        <Typography sx={{
          fontFamily: 'Heebo, sans-serif',
          fontSize: '0.72rem',
          fontWeight: 700,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: '#9a7209',
          mb: 1.2,
        }}>
          אפשרויות הצטיידות
        </Typography>

        {HW_TIERS.map((tier) => {
          const { line1, line2 } = bundlePrice(plan, tier);
          return (
            <Box
              key={tier.key}
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: 1,
                py: 0.65,
                borderBottom: `1px dashed rgba(30,58,95,0.06)`,
                '&:last-child': { borderBottom: 'none' },
              }}
            >
              {/* Label + note */}
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                  <Typography sx={{
                    fontFamily: 'Heebo, sans-serif',
                    fontSize: '0.78rem',
                    fontWeight: tier.key === 'software' ? 400 : 600,
                    color: textMain,
                    opacity: tier.key === 'software' ? 0.7 : 1,
                  }}>
                    {tier.label}
                  </Typography>
                  {tier.badge && (
                    <Box sx={{
                      bgcolor: 'rgba(154,114,9,0.12)',
                      color: '#9a7209',
                      fontFamily: 'Heebo, sans-serif',
                      fontWeight: 700,
                      fontSize: '0.62rem',
                      px: 0.7,
                      py: 0.1,
                      borderRadius: 0.8,
                    }}>
                      {tier.badge}
                    </Box>
                  )}
                </Box>
                <Typography sx={{
                  fontFamily: 'Heebo, sans-serif',
                  fontSize: '0.68rem',
                  color: textMuted,
                  lineHeight: 1.3,
                }}>
                  {tier.note}
                </Typography>
              </Box>

              {/* Price column */}
              <Box sx={{ textAlign: 'left', flexShrink: 0 }}>
                <Typography sx={{
                  fontFamily: 'Heebo, sans-serif',
                  fontSize: '0.78rem',
                  fontWeight: 700,
                  color: textMain,
                  opacity: tier.key === 'software' ? 0.7 : 1,
                  whiteSpace: 'nowrap',
                }}>
                  {line1}
                </Typography>
                {line2 && (
                  <Typography sx={{
                    fontFamily: 'Heebo, sans-serif',
                    fontSize: '0.68rem',
                    color: textMuted,
                    whiteSpace: 'nowrap',
                  }}>
                    {line2}
                  </Typography>
                )}
              </Box>
            </Box>
          );
        })}

        <Typography sx={{
          fontFamily: 'Heebo, sans-serif',
          fontSize: '0.65rem',
          color: textMuted,
          mt: 1,
          lineHeight: 1.5,
          opacity: 0.75,
        }}>
          חומרה בעלות בלבד · ניתן לרכוש ציוד עצמאית בכל עת
        </Typography>
      </Box>}

      {/* CTA */}
      <Button
        variant="outlined"
        fullWidth
        onClick={() => onCta(plan.key)}
        sx={{
          bgcolor: 'transparent',
          borderColor: 'rgba(30,58,95,0.25)',
          color: '#1e3a5f',
          fontFamily: 'Heebo, sans-serif',
          fontWeight: 600,
          fontSize: '0.95rem',
          py: 1.25,
          borderRadius: 2,
          '&:hover': {
            bgcolor: 'rgba(30,58,95,0.05)',
            borderColor: '#1e3a5f',
          },
          transition: 'all 0.2s ease',
        }}
      >
        {plan.cta}
      </Button>
    </Box>
  );
};

const PricingSection = ({ onOpenModal }) => {
  const handleCta = (plan) => {
    if (plan.key === 'trial') {
      // Trial — just go to login/signup
      window.location.href = '/login';
      return;
    }
    onOpenModal?.('contact', {
      planName: plan.name,
      subject: `בקשה למסלול ${plan.name}`,
      message: `שלום,\nאני מעוניין/ת במסלול "${plan.name}" של inorai.\n\nפרטים נוספים:\n`,
    });
  };

  return (
    <Box id="pricing" sx={{ bgcolor: '#fcfbfa', py: { xs: 10, md: 14 }, px: { xs: 3, md: 6 } }}>
      <Box sx={{ maxWidth: '1200px', mx: 'auto' }}>

        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Typography sx={{
            fontFamily: 'Secular One, sans-serif',
            color: '#9a7209',
            fontSize: '0.85rem',
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            mb: 2,
          }}>
            מסלולים
          </Typography>
          <Typography
            component="h2"
            dir="rtl"
            sx={{
              fontFamily: 'Frank Ruhl Libre, serif',
              color: '#1e3a5f',
              fontSize: { xs: '1.9rem', md: '2.5rem' },
              fontWeight: 900,
              letterSpacing: '-0.02em',
              mb: 2,
              direction: 'rtl',
            }}
          >
            שקוף, <Box component="span" className="lp-gold-text">פשוט</Box>, ללא הפתעות
          </Typography>
          <Typography sx={{
            color: '#5a6978',
            fontFamily: 'Heebo, sans-serif',
            fontSize: '1rem',
            maxWidth: '480px',
            mx: 'auto',
            lineHeight: 1.8,
          }}>
            מתחילים עם 14 יום חינם — ממשיכים עם המסלול שמתאים לכם.
            ניתן לשדרג בכל עת. ללא חוזים.
          </Typography>
        </Box>

        {/* Pioneer banner */}
        <PioneerBanner onOpenModal={onOpenModal} />

        {/* Plans grid */}
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' },
          gap: 3,
          alignItems: 'start',
        }}>
          {PLANS.map(plan => (
            <PlanCard key={plan.key} plan={plan} onCta={() => handleCta(plan)} />
          ))}
        </Box>

        <Typography sx={{
          textAlign: 'center',
          color: '#a0a9b4',
          fontFamily: 'Heebo, sans-serif',
          fontSize: '0.85rem',
          mt: 5,
        }}>
          כל המחירים כוללים מע"מ · ניתן לשדרג, לשנמך, או לבטל בכל עת
        </Typography>
      </Box>
    </Box>
  );
};

export default PricingSection;
